// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import './Histories.scss';
import { useState } from 'react';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { HistoryCollected } from 'components/organisms/HistoryCollected';
import { HistoryCollectComplete } from 'components/organisms/HistoryCollectComplete';
import { HistoryRecycleReceives } from 'components/organisms/HistoryRecycleReceives';
import { HistoryRecycleCompletes } from 'components/organisms/HistoryRecycleCompletes';

import { DownloadModal } from 'components/organisms/DownloadModal';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import {
  CollectDelivery,
  CollectHistoryListResponse,
  CompressionCompleteData,
  CompressionReceiveData,
  EdiStatus,
  RecycleReceiveData,
} from 'util/Types';
import { OperatorType } from 'util/Enums';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { ApiInfo } from 'components/organisms/DownloadModal';
import { TabletTab } from 'components/molecules/TabletTab';
import { DATE_FORMAT } from 'util/ConstantValues';
import { useTranslation } from 'react-i18next';
import _snakeCase from 'lodash/snakeCase';
import { getCollectTenantMapper, getEdiStatusLabel } from 'util/commons';
import { useHistory } from 'react-router';
import { createAxios } from 'ts/createAxios';
import { getCollectDeliveries } from 'apis/collects';
import Csv from 'util/csv';
import { store } from 'redux/store';
import { toast } from 'react-toastify';
import { setIsExporting } from 'redux/slices/historiesCsvExportSlice';
import { handleError } from 'util/errorHandler';
import dayjs from 'dayjs';

const Histories = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const tabNames = {
    Collects: [
      t('histories.tabs.collect_list'),
      t('histories.tabs.collect_delivery_completed_list'),
    ],
    Compressions: [
      t('histories.tabs.compression_receipt_list'),
      t('histories.tabs.compression_shipping_registration_list'),
    ],
    Recycle: [
      t('histories.tabs.recycle_receipt_list'),
      t('histories.tabs.recycle_product_registration'),
    ],
  };

  const apiInfo: {
    [K in keyof typeof OperatorType]?: [ApiInfo, ApiInfo];
  } = {
    Collects: [
      {
        globally: true,
        asyncExport: async ({ onClose, showSuccessToast }) => {
          onClose();

          const res = await createAxios({
            prefix: 'v2',
            notShowLoading: true,
          }).get<CollectHistoryListResponse>(
            '/work-histories/collect/receives',
            {
              params: {
                page: 0,
                ...(collectReceivedDateRange && {
                  dateFrom: dayjs(collectReceivedDateRange[0]).format(
                    DATE_FORMAT.full_date
                  ),
                  dateTo: dayjs(collectReceivedDateRange[1]).format(
                    DATE_FORMAT.full_date
                  ),
                }),
                ...(collectReceivedDriverId && {
                  deliveryUserIds: collectReceivedDriverId,
                }),
              },
            }
          );

          const data = res.data.deliveries;
          let deliveriesData: CollectDelivery[] = [];

          await getCollectDeliveries({
            deliveryIds: data.map((c) => c.id),
            cursor: null,
            showLoading: false,
            type: 'globalExport',
            successCallback: (deliveries) => {
              deliveriesData = deliveries;
            },
          });

          const byTenantMap = getCollectTenantMapper(deliveriesData);

          const headers = [
            t('histories.label_csv_collecting_date'),
            t('histories.label_csv_collecting_time'),
            t('histories.label_csv_responsible_person'),
            t('histories.label_csv_place'),
            t('histories.label_csv_tenant_name'),
            t('histories.label_csv_material_name'),
            t('common_history_field.item'),
            t('common_history_field.emission_registered_weight') + ' (kg)',
            t('histories.label_csv_emission_id'),
            t('histories.label_edi_id'),
            t('histories.label_manifest_number'),
          ];

          const csvData = data.reduce((acc: string[][], collect) => {
            Object.entries(byTenantMap[collect.id] || {})
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .sort(([_, a], [__, b]) => a.name.localeCompare(b.name))
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .forEach(([_, tenantData]) => {
                acc.push([
                  getFormatDateWithCurrentTimezone(
                    collect.receivedDatetime,
                    DATE_FORMAT.full_date
                  ),
                  getFormatDateWithCurrentTimezone(
                    collect.receivedDatetime,
                    DATE_FORMAT.hhss
                  ),
                  collect.userId + '(' + collect.userName + ')',
                  collect.areaName,
                  tenantData.name || '',
                  collect.description,
                  collect.wasteUnitName || '',
                  tenantData.weight || '',
                  (tenantData.processingObjectIds || []).join('\n'),
                  getEdiStatusLabel({
                    status: collect.receivedEdiStatus,
                    t,
                    operatorType: OperatorType.Collects,
                  }) || '',
                  collect.receivedEdiStatus != null &&
                  collect.receivedEdiStatus !== EdiStatus.None &&
                  collect.ediNumber != null
                    ? collect.ediNumber
                    : '',
                ]);
              });

            return acc;
          }, []);

          if (store.getState().historiesCsvExport.isExporting) {
            showSuccessToast();
            new Csv({
              data: csvData,
              headers,
            }).downLoadCsv({
              filename:
                t('histories.title') + '（' + tabNames.Collects[0] + '）',
            });
          }
        },
      },
      {
        globally: true,
        asyncExport: async ({ onClose, showSuccessToast }) => {
          onClose();
          const res = await createAxios({
            prefix: 'v2',
            notShowLoading: true,
          }).get<CollectHistoryListResponse>(
            '/work-histories/collect/delivered',
            {
              params: {
                page: 0,
                ...(collectDeliveredDateRange && {
                  dateFrom: dayjs(collectDeliveredDateRange[0]).format(
                    DATE_FORMAT.full_date
                  ),
                  dateTo: dayjs(collectDeliveredDateRange[1]).format(
                    DATE_FORMAT.full_date
                  ),
                }),
                ...(collectDeliveredDriverId && {
                  deliveryUserIds: collectDeliveredDriverId,
                }),
              },
            }
          );

          const data = res.data.deliveries;
          let deliveriesData: CollectDelivery[] = [];

          await getCollectDeliveries({
            deliveryIds: data.map((c) => c.id),
            cursor: null,
            showLoading: false,
            type: 'globalExport',
            successCallback: (deliveries) => {
              deliveriesData = deliveries;
            },
          });

          const byTenantMap = getCollectTenantMapper(deliveriesData);

          const headers = [
            t('histories.label_csv_collecting_date'),
            t('histories.label_csv_collecting_time'),
            t('histories.label_csv_delivery_date'),
            t('histories.label_csv_delivery_time'),
            t('histories.label_csv_responsible_person'),
            t('histories.label_csv_place'),
            t('histories.label_csv_tenant_name'),
            t('histories.label_csv_material_name'),
            t('common_history_field.item'),
            t('common_history_field.emission_registered_weight') + ' (kg)',
            t('histories.label_csv_emission_id'),
          ];

          const csvData = data.reduce((acc: string[][], collect) => {
            Object.entries(byTenantMap[collect.id] || {})
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .sort(([_, a], [__, b]) => a.name.localeCompare(b.name))
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .forEach(([_, tenantData]) => {
                acc.push([
                  getFormatDateWithCurrentTimezone(
                    collect.receivedDatetime,
                    DATE_FORMAT.full_date
                  ),
                  getFormatDateWithCurrentTimezone(
                    collect.receivedDatetime,
                    DATE_FORMAT.hhss
                  ),
                  collect.deliveredDatetime
                    ? getFormatDateWithCurrentTimezone(
                        collect.deliveredDatetime,
                        DATE_FORMAT.full_date
                      )
                    : '',
                  collect.deliveredDatetime
                    ? getFormatDateWithCurrentTimezone(
                        collect.deliveredDatetime,
                        DATE_FORMAT.hhss
                      )
                    : '',
                  collect.userId + '(' + collect.userName + ')',
                  collect.areaName,
                  tenantData.name || '',
                  collect.description,
                  collect.wasteUnitName || '',
                  tenantData.weight || '',
                  (tenantData.processingObjectIds || []).join('\n'),
                ]);
              });

            return acc;
          }, []);

          if (store.getState().historiesCsvExport.isExporting) {
            showSuccessToast();
            new Csv({
              data: csvData,
              headers,
            }).downLoadCsv({
              filename:
                t('histories.title') + '（' + tabNames.Collects[1] + '）',
            });
          }
        },
      },
    ],
    Compressions: [
      {
        fileName: t('histories.title') + '（' + tabNames.Compressions[0] + '）',
        url: '/work-histories/compression/receives?',
        axiosConfig: {
          prefix: 'v2',
        },
        header: [
          t('histories.label_csv_received_date_time'),
          t('histories.label_csv_responsible_person'),
          t('histories.label_csv_place'),
          t('histories.label_csv_item_name'),
          t('histories.label_csv_material_name'),
          t('histories.label_csv_weight_kg'),
          t('histories.label_csv_reception_method'),
          t('histories.label_csv_resource_id'),
          t('histories.label_edi_id'),
          t('histories.label_manifest_number'),
        ],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createCsv: (data: CompressionReceiveData[]) => {
          return data.map((item) => [
            getFormatDateWithCurrentTimezone(
              item.receivedDatetime,
              DATE_FORMAT.slaYMDHm
            ),
            item.userId + '(' + item.userName + ')',
            item.deliveryFromSiteName,
            item.wasteUnitName || '',
            item.description,
            item.weight,
            t(`histories.${_snakeCase(item.receptionMethod)}`),
            item.targetIds.map(({ id }) => id).join('\n'),
            item.targetIds[0].ediStatus != null &&
            item.targetIds[0].ediStatus !== EdiStatus.None
              ? getEdiStatusLabel({
                  status: item.targetIds[0].ediStatus,
                  t,
                })
              : '',
            item.targetIds[0].ediStatus != null &&
            item.targetIds[0].ediStatus !== EdiStatus.None &&
            item.targetIds[0].ediNumber != null
              ? item.targetIds[0].ediNumber
              : '',
          ]);
        },
      },
      {
        fileName: t('histories.title') + '（' + tabNames.Compressions[1] + '）',
        url: '/work-histories/compression/delivered?',
        axiosConfig: {
          prefix: 'v2',
        },
        header: [
          t('histories.label_csv_requested_date_time'),
          t('histories.label_csv_processed_date_time'),
          t('histories.label_csv_responsible_person'),
          t('histories.label_csv_shipping_address'),
          t('histories.label_csv_item_name'),
          t('histories.label_csv_material_name'),
          t('histories.label_csv_shipping_quality'),
          `${t('pre_regist_resource_select.input_weight')} (kg)`,
          t('histories.label_csv_weight_kg'),
          t('histories.label_csv_resource_id'),
        ],
        createCsv: (data: CompressionCompleteData[]) => {
          return data.reduce((acc: string[][], cur) => {
            for (const key in cur.byCompression) {
              acc.push([
                getFormatDateWithCurrentTimezone(
                  cur.requestedDatetime,
                  DATE_FORMAT.slaYMDHm
                ),
                getFormatDateWithCurrentTimezone(
                  cur.byCompression[key].registeredDatetime,
                  DATE_FORMAT.slaYMDHm
                ),
                cur.userId + '(' + cur.userName + ')',
                cur.deliveryToOperatorName,
                cur.wasteUnitName || '',
                cur.description,
                cur.byCompression[key].grade,
                cur.byCompression[key].totalInputWeight,
                cur.byCompression[key].weight,
                key,
              ]);
            }
            return acc;
          }, []);
        },
      },
    ],
    Recycle: [
      {
        fileName: t('histories.title') + '（' + tabNames.Recycle[0] + '）',
        url: '/work-histories/recycle/receives?',
        axiosConfig: {
          prefix: 'v2',
        },
        header: [
          t('histories.label_csv_date_time'),
          t('histories.label_csv_responsible_person'),
          t('histories.label_csv_shipping_origin'),
          t('histories.label_csv_material_name'),
          t('histories.label_csv_weight_kg'),
          t('histories.label_csv_reception_method'),
          t('histories.label_csv_volume_reduction_id'),
          t('histories.label_edi_id'),
          t('histories.label_manifest_number'),
        ],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createCsv: (data: RecycleReceiveData[]) => {
          return data.map((item) => [
            getFormatDateWithCurrentTimezone(
              item.datetime as string,
              DATE_FORMAT.slaYMDHm
            ),
            item.userId + '(' + item.userName + ')',
            item.deliveryFromOperatorName,
            item.description,
            item.weight,
            t(`histories.${_snakeCase(item.receptionMethod)}`),
            item.targetIds.map(({ id }) => id).join('\n'),
            item.targetIds[0].ediStatus != null &&
            item.targetIds[0].ediStatus !== EdiStatus.None
              ? getEdiStatusLabel({
                  status: item.targetIds[0].ediStatus,
                  t,
                })
              : '',
            item.targetIds[0].ediStatus != null &&
            item.targetIds[0].ediStatus !== EdiStatus.None &&
            item.targetIds[0].ediNumber != null
              ? item.targetIds[0].ediNumber
              : '',
          ]);
        },
      },
      {
        fileName: t('histories.title') + '（' + tabNames.Recycle[1] + '）',
        url: '/work-histories/recycle/complete?',
        axiosConfig: {
          prefix: 'v2',
        },
        header: [
          t('histories.label_csv_date_time'),
          t('histories.label_csv_responsible_person'),
          t('histories.label_csv_material_name'),
          t('histories.label_csv_kind'),
          t('histories.label_csv_shipping_quality'),
          t('histories.label_csv_weight_kg'),
          t('histories.label_csv_product_id'),
        ],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createCsv: (data: any[]) => {
          return data.map((item) => [
            getFormatDateWithCurrentTimezone(
              item.datetime as string,
              DATE_FORMAT.slaYMDHm
            ),
            item.userId + '(' + item.userName + ')',
            item.description,
            item.materialType.name,
            item.grade.name,
            item.weight,
            item.targetIds.join('\n'),
          ]);
        },
      },
    ],
  };
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState<number>(
    history.location.hash === '#1' ? 1 : 0
  );
  const [isDownloadModalOpen, setIsDownloadModalOpen] =
    useState<boolean>(false);
  const [collectReceivedDateRange, setCollectReceivedDateRange] = useState<
    [string, string] | undefined
  >();
  const [collectReceivedDriverId, setCollectReceivedDriverId] = useState<
    string | undefined
  >();
  const [collectDeliveredDateRange, setCollectDeliveredDateRange] = useState<
    [string, string] | undefined
  >();
  const [collectDeliveredDriverId, setCollectDeliveredDriverId] = useState<
    string | undefined
  >();

  function getApiInfo(): ApiInfo | undefined {
    return apiInfo[user.operatorType]?.[tabIndex];
  }

  const closeDownloadModal = () => {
    setIsDownloadModalOpen(false);
  };
  const openDownloadModal = () => {
    setIsDownloadModalOpen(true);
  };

  const handleDirectCollectExport = async () => {
    const apiInfoItem = getApiInfo();
    if (!apiInfoItem || !('asyncExport' in apiInfoItem)) return;

    const toastId = toast.info(t('messages.M_file_export_started'));
    dispatch(setIsExporting(true));

    try {
      await apiInfoItem.asyncExport({
        onClose: () => {},
        showSuccessToast: () =>
          toast.success(t('messages.M_file_export_finished')),
      });
    } catch (e) {
      handleError(e);
    } finally {
      dispatch(setIsExporting(false));
      toast.dismiss(toastId);
    }
  };

  return (
    <div>
      <PrimaryTemplate
        h1={
          user.operatorType == OperatorType.Compressions ||
          user.operatorType == OperatorType.Recycle
            ? t('histories.processing_title')
            : t('histories.title')
        }
        download={
          user.operatorType === OperatorType.Collects
            ? handleDirectCollectExport
            : openDownloadModal
        }
        className="history"
      >
        <TabletTab
          tabItems={[
            tabNames[user.operatorType][0],
            tabNames[user.operatorType][1],
          ]}
          setTabIndex={setTabIndex}
          defaultTabIndex={tabIndex}
          renderContent={({ index }) => {
            {
              if (user.operatorType == OperatorType.Collects) {
                return (
                  <div className="scroll-contents">
                    {index === 0 && (
                      <HistoryCollected
                        onDateRangeChange={setCollectReceivedDateRange}
                        onDriverIdChange={setCollectReceivedDriverId}
                      />
                    )}
                    {index === 1 && (
                      <HistoryCollectComplete
                        onDateRangeChange={setCollectDeliveredDateRange}
                        onDriverIdChange={setCollectDeliveredDriverId}
                      />
                    )}
                  </div>
                );
              } else if (user.operatorType == OperatorType.Recycle) {
                return (
                  <div className="scroll-contents">
                    {index === 0 && <HistoryRecycleReceives />}
                    {index === 1 && <HistoryRecycleCompletes />}
                  </div>
                );
              }
            }
          }}
        />
      </PrimaryTemplate>
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={closeDownloadModal}
        apiInfo={getApiInfo() as ApiInfo}
      />
    </div>
  );
};

export default Histories;
